import {
	createContext,
	useContext,
	useState,
	useCallback,
	useMemo,
} from '@wordpress/element';
import { api } from '../../helpers/api';
import type {
	Booking,
	CancellationReasonMode,
	Resource,
} from '../../typings/common/booking';
import { useRescheduleMutation, useCancelMutation } from '../mutations';

interface APIError {
	message: string;
}

interface ManagementContextType {
	// Booking state
	bookings: Booking[];
	isLoading: boolean;
	error: string | null;

	/** Whether the cancellation reason field is hidden, optional or required. */
	cancellationReason: CancellationReasonMode;

	// Actions
	cancelBooking: (bookingId: number, reason?: string) => Promise<void>;
	rescheduleBooking: (
		bookingId: number,
		startDateTime: string,
		endDateTime?: string,
		resourceIds?: number[]
	) => Promise<void>;
}

const ManagementContext = createContext<ManagementContextType | undefined>(
	undefined
);

interface ProviderProps {
	managementKey: string;
	bookings: Booking[];
	cancellationReason?: CancellationReasonMode;
	children: React.ReactNode;
}

export function ManagementProvider({
	managementKey,
	bookings: initialBookings,
	cancellationReason = 'off',
	children,
}: ProviderProps) {
	const [bookings, setBookings] = useState<Booking[]>(initialBookings);
	const [error, setError] = useState<string | null>(null);

	const cancelMutation = useCancelMutation();
	const rescheduleMutation = useRescheduleMutation();

	const isLoading = cancelMutation.isPending || rescheduleMutation.isPending;

	const cancelBooking = useCallback(
		async (bookingId: number, reason?: string) => {
			setError(null);

			try {
				await cancelMutation.mutateAsync({
					data: {
						key: managementKey,
						booking_id: bookingId,
						...(reason?.trim() && { reason: reason.trim() }),
					},
				});

				// Update local state for both service and resource bookings
				setBookings((prevBookings) =>
					prevBookings.map((booking) =>
						booking.id === bookingId ||
						booking.parentBookingId === bookingId
							? { ...booking, status: 'cancelled' }
							: booking
					)
				);
			} catch (err) {
				const error = err as APIError;
				setError(error.message || 'Failed to cancel booking.');
				throw err;
			}
		},
		[managementKey, cancelMutation]
	);

	const rescheduleBooking = useCallback(
		async (
			bookingId: number,
			startDateTime: string,
			endDateTime?: string,
			resourceIds?: number[]
		) => {
			setError(null);

			try {
				const response = await rescheduleMutation.mutateAsync({
					data: {
						key: managementKey,
						booking_id: bookingId,
						start_datetime: startDateTime,
						...(endDateTime && {
							end_datetime: endDateTime,
						}),
						...(Array.isArray(resourceIds) && {
							resource_ids: resourceIds,
						}),
					},
				});

				const selectedResourceId =
					Array.isArray(resourceIds) && resourceIds.length === 1
						? resourceIds[0]
						: null;

				let selectedResource: Resource | undefined;
				if (selectedResourceId !== null) {
					const serviceId =
						bookings.find(
							(booking) =>
								booking.id === bookingId ||
								booking.parentBookingId === bookingId
						)?.serviceId ?? 0;

					if (serviceId > 0) {
						try {
							const resources = await api.get(
								'/gp-bookings/v1/services/:id/resources',
								{
									params: { id: serviceId },
								}
							);
							selectedResource = resources.find(
								(resource) => resource.id === selectedResourceId
							);
						} catch (e) {
							// eslint-disable-next-line no-console
							console.warn(
								'Failed to fetch resources for booking management sync:',
								e
							);
						}
					}
				}

				const serviceBookingId =
					response.objectType === 'resource' &&
					response.parentBookingId
						? response.parentBookingId
						: response.id;

				// Update local state for both service and resource bookings
				setBookings((prevBookings) =>
					prevBookings.map((booking) => {
						if (
							booking.id === serviceBookingId ||
							booking.parentBookingId === serviceBookingId
						) {
							const nextBooking: Booking = {
								...booking,
								start: response.start,
								end: response.end,
								status: response.status,
							};

							if (
								selectedResourceId !== null &&
								booking.objectType === 'resource' &&
								booking.parentBookingId === serviceBookingId
							) {
								nextBooking.objectId = selectedResourceId;
								if (selectedResource) {
									nextBooking.objectTitle =
										selectedResource.name;
									nextBooking.color =
										selectedResource.color ?? undefined;
								}
							}

							return nextBooking;
						}
						return booking;
					})
				);
			} catch (err) {
				const error = err as APIError;
				setError(error.message || 'Failed to reschedule booking.');
				throw err;
			}
		},
		[managementKey, rescheduleMutation, bookings]
	);

	const value = useMemo(() => {
		return {
			bookings,
			isLoading,
			error,
			cancellationReason,
			cancelBooking,
			rescheduleBooking,
		};
	}, [
		bookings,
		isLoading,
		error,
		cancellationReason,
		cancelBooking,
		rescheduleBooking,
	]);

	return (
		<ManagementContext.Provider value={value}>
			{children}
		</ManagementContext.Provider>
	);
}

export function useManagement() {
	const context = useContext(ManagementContext);
	if (context === undefined) {
		throw new Error(
			'useManagement must be used within a ManagementProvider'
		);
	}
	return context;
}
